import { Injectable } from '@angular/core';
import { cloneDeep } from 'lodash-es';
import { MockApiService } from '@zopup/zop-core';
import { WorkDefinitionDto } from '@zopup/zop-types';
import { generateMockWorkDefinitions } from './data';

// The real WorkDefinitionsService calls an absolute URL (`${MANUFACTURING_API_BASE_PATH}/WorkDefinitions/...`).
// `:scheme` / `:host` are wildcard segments (MockApiService treats any `:`-prefixed
// segment as "match anything") so these handlers work regardless of which environment
// (local gateway, Azure dev container, etc.) resolves that base path.
const workDefinitionsUrl = (path = ''): string => `:scheme//:host/WorkDefinitions${path}`;

@Injectable({ providedIn: 'root' })
export class WorkDefinitionsMockApi {
    private _workDefinitions: WorkDefinitionDto[] = generateMockWorkDefinitions();

    constructor(private _zopMockApiService: MockApiService) {
        this.registerHandlers();
    }

    registerHandlers(): void {
        // -----------------------------------------------------------------------------------------------------
        // @ WorkDefinitions - GET (paged list) - registered before the `:id` handler so the literal
        // "GetPagedList" / "Resolve" segments are matched first
        // -----------------------------------------------------------------------------------------------------
        this._zopMockApiService.onGet(workDefinitionsUrl('/GetPagedList')).reply(({ request }) => {
            const searchText = (request.params.get('SearchText') || '').toLowerCase();
            const pageNumber = Number(request.params.get('PageNumber')) || 1;
            const pageSize = Number(request.params.get('PageSize')) || 50;
            const orderBy = request.params.getAll('OrderBy') || [];
            const isDescending = request.params.get('IsOrderByDescending') === 'true';

            let rows = cloneDeep(this._workDefinitions);

            if (searchText) {
                rows = rows.filter(
                    (def) =>
                        def.code?.toLowerCase().includes(searchText) ||
                        def.revision?.toLowerCase().includes(searchText) ||
                        def.workOrderType?.toLowerCase().includes(searchText) ||
                        def.policyCode?.toLowerCase().includes(searchText)
                );
            }

            if (orderBy.length) {
                const [field] = orderBy;
                rows.sort((a, b) => {
                    const aValue = String((a as any)[field] ?? '');
                    const bValue = String((b as any)[field] ?? '');
                    return isDescending ? bValue.localeCompare(aValue) : aValue.localeCompare(bValue);
                });
            }

            const totalCount = rows.length;
            const start = (pageNumber - 1) * pageSize;
            const paged = rows.slice(start, start + pageSize);

            return [200, { isSuccess: true, result: paged, totalCount }];
        });

        // -----------------------------------------------------------------------------------------------------
        // @ WorkDefinitions - GET by id
        // -----------------------------------------------------------------------------------------------------
        this._zopMockApiService.onGet(workDefinitionsUrl('/:id')).reply(({ urlParams }) => {
            const definition = this._workDefinitions.find((d) => d.id === urlParams['id']);
            if (!definition) {
                return [200, { isSuccess: false, errors: [{ message: 'Work definition not found' }] }];
            }
            return [200, { isSuccess: true, result: cloneDeep(definition) }];
        });

        // -----------------------------------------------------------------------------------------------------
        // @ WorkDefinitions - POST (create)
        // -----------------------------------------------------------------------------------------------------
        this._zopMockApiService.onPost(workDefinitionsUrl()).reply(({ request }) => {
            const definitionInput = request.body?.definition ?? {};
            const newDefinition: WorkDefinitionDto = {
                id: `wd-${this._workDefinitions.length + 1}-${Date.now()}`,
                status: 'Draft',
                version: 1,
                ...definitionInput,
            };
            this._workDefinitions = [newDefinition, ...this._workDefinitions];
            return [200, { isSuccess: true, result: cloneDeep(newDefinition) }];
        });

        // -----------------------------------------------------------------------------------------------------
        // @ WorkDefinitions - PUT Revise (adds a new draft revision under the same code)
        // -----------------------------------------------------------------------------------------------------
        this._zopMockApiService.onPut(workDefinitionsUrl('/Revise')).reply(({ request }) => {
            const body = request.body as { id?: string; version?: number; revision?: Partial<WorkDefinitionDto> } | undefined;
            const source = this._workDefinitions.find((d) => d.id === body?.id);
            if (!source) {
                return [200, { isSuccess: false, errors: [{ message: 'Work definition not found' }] }];
            }
            const revised: WorkDefinitionDto = {
                ...source,
                ...body?.revision,
                id: `wd-${this._workDefinitions.length + 1}-${Date.now()}`,
                code: source.code,
                status: 'Draft',
                version: 1,
                activatedAt: undefined,
            };
            this._workDefinitions = [revised, ...this._workDefinitions];
            return [200, { isSuccess: true, result: cloneDeep(revised) }];
        });

        // -----------------------------------------------------------------------------------------------------
        // @ WorkDefinitions - PUT Activate / Obsolete
        // -----------------------------------------------------------------------------------------------------
        this._zopMockApiService.onPut(workDefinitionsUrl('/Activate')).reply(({ request }) => this._setStatus(request.body, 'Active'));
        this._zopMockApiService.onPut(workDefinitionsUrl('/Obsolete')).reply(({ request }) => this._setStatus(request.body, 'Obsolete'));
    }

    private _setStatus(body: { id?: string; version?: number } | undefined, status: WorkDefinitionDto['status']): [number, unknown] {
        const definition = this._workDefinitions.find((d) => d.id === body?.id);
        if (!definition) {
            return [200, { isSuccess: false, errors: [{ message: 'Work definition not found' }] }];
        }
        definition.status = status;
        definition.version = (definition.version ?? 1) + 1;
        if (status === 'Active' && !definition.activatedAt) {
            definition.activatedAt = new Date();
        }
        return [200, { isSuccess: true, result: cloneDeep(definition) }];
    }
}
